<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { isAppCapableOf } from '@@/bits/app_can'
import { ContentPickerSource } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { ActionType, handleAction } from '@@/bits/whiteboard'
import { isPadletTool, toContentSource } from '@@/bits/whiteboard_tools'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import { openNativeWhiteboardContentPicker } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceWindowMobileNotchStore } from '@@/pinia/surface_window_mobile_notch'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import WhiteboardToolbarIcon from '@@/vuecomponents/WhiteboardToolbarIcon.vue'
import { useSurfaceAttachmentPicker } from '@@/vuecomposables/surface_attachment_picker'
import { storeToRefs } from 'pinia'
import { react } from 'tldraw'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

defineEmits<{
  (e: 'pointerdown', event: PointerEvent): void
}>()

const surfaceStore = useSurfaceStore()
const whiteboardStore = useWhiteboardStore()

const { isLoadingPortraitMobileLayout } = storeToRefs(whiteboardStore)
const whiteboardToolbarStore = useWhiteboardToolbarStore()

const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const { isContainerSmallerThanTabletPortrait } = storeToRefs(surfaceContainerSizeStore)

/**
 * COLOR SCHEME
 * Currently follow background luminance of surface (based on walllpaper)
 */
const colorScheme = computed((): 'dark' | 'light' => {
  return surfaceStore.backgroundLuminance
})

// Tooltip

const { trackToolHoverOn, trackToolHoverOff } = whiteboardToolbarStore
const { xTooltip, tooltipToolId, isSingleRowLayout, isSingleColumnLayout, isDoubleColumnLayout } =
  storeToRefs(whiteboardToolbarStore)

/**
 * TOOL LOGIC
 */

let hideSecondaryMenuListener: () => void = () => {}

onMounted(() => {
  hideSecondaryMenuListener = react('hide secondary menu when drawing/editing objects', () => {
    const editor = getEditorObject()
    if (editor?.isInAny('draw.drawing', 'highlight.drawing', 'padlet-draw-eraser.erasing')) {
      whiteboardToolbarStore.xSecondaryMenu = false
    }
  })
})

onUnmounted(() => {
  hideSecondaryMenuListener()
})

const toolDisabledState = computed(() => ({
  undo: !whiteboardStore.canIUndo,
  redo: !whiteboardStore.canIRedo,
}))

const { pickContentSourceIfSupported } = useSurfaceAttachmentPicker()

function handleSelectTool(event: PointerEvent, toolId: string): void {
  trackEvent('Whiteboard', 'Clicked toolbar tool', toolId, null, { context: 'Toolbar' })

  const isClickingOnAlreadySelectedTool = whiteboardStore.selectedToolId === toolId
  if (isClickingOnAlreadySelectedTool) {
    whiteboardToolbarStore.xSecondaryMenu = !whiteboardToolbarStore.xSecondaryMenu
  } else {
    whiteboardToolbarStore.xSecondaryMenu = true
  }

  // Attachment tool will switch tldraw to select tool and then show the content picker
  switch (toolId) {
    case 'padlet-file':
    case 'padlet-media_library':
      whiteboardStore.selectTool('select')
      if (device.app && isAppCapableOf('nativeWhiteboardContentPicker')) {
        postMessage(openNativeWhiteboardContentPicker({ sourceId: toContentSource(toolId) }))
        return
      }
      useSurfaceContentPickerStore().showPostContentPicker()
      useContentPickerStore().showContentSourceMenu({
        isHiddenMenu: true,
        contentPickerContext: 'whiteboardUpload',
      })
      nextTick(() => {
        useContentPickerStore().triggerHiddenInput(ContentPickerSource.FileBrowser)
      })
      return
    case 'padlet-attachment':
      whiteboardStore.selectTool('select')
      if (device.app && isAppCapableOf('nativeWhiteboardContentPicker')) {
        postMessage(openNativeWhiteboardContentPicker())
        return
      }
      useSurfaceContentPickerStore().showPostContentPicker()
      useContentPickerStore().showContentSourceMenu({ contentPickerContext: 'whiteboardUpload' })
      return
    case 'undo':
      handleAction(ActionType.Undo, () => whiteboardStore.canIUndo)
      return
    case 'redo':
      handleAction(ActionType.Redo, () => whiteboardStore.canIRedo)
      return
  }

  if (isPadletTool(toolId)) {
    whiteboardStore.selectTool('select')
    if (device.app && isAppCapableOf('nativeWhiteboardContentPicker')) {
      postMessage(openNativeWhiteboardContentPicker({ sourceId: toContentSource(toolId) }))
      return
    }

    useSurfaceContentPickerStore().showPostContentPicker()
    useContentPickerStore().showContentSourceMenu({
      isHiddenMenu: true,
      contentPickerContext: 'whiteboardUpload',
    })
    const contentSource = toContentSource(toolId)
    if (contentSource != null) {
      nextTick(() => {
        // pickContentSourceIfSupported won't handle these sources on mobile because
        // it was specifically built for UPE. We need to handle them separately.
        if (contentSource === ContentPickerSource.PhotoBooth && device.mobile) {
          useContentPickerStore().triggerHiddenInput(contentSource)
        } else {
          pickContentSourceIfSupported(contentSource)
        }
      })
      return
    }
  }

  // Fall back to Tldraw tool if none Padlet tool matches
  // Use currentTarget, not target, otherwise the selected element can vary
  whiteboardStore.setSelectedToolbarButtonEl(event.currentTarget as HTMLElement)
  whiteboardStore.selectTool(toolId)
}

const toolbar = ref<HTMLElement | null>(null)

watch(
  isContainerSmallerThanTabletPortrait,
  (isSmaller) => {
    if (isSmaller) {
      nextTick(() => {
        if (toolbar.value) {
          toolbar.value.scrollLeft = toolbar.value.scrollWidth / 2 - toolbar.value.clientWidth / 2
          toolbar.value.scrollTop = toolbar.value.scrollHeight / 2 - toolbar.value.clientHeight / 2
        }
      })
    }
  },
  { immediate: true },
)

const surfaceWindowMobileNotchStore = useSurfaceWindowMobileNotchStore()
</script>

<template>
  <Transition :name="!isLoadingPortraitMobileLayout ? 'slide' : ''" appear>
    <div
      ref="toolbar"
      data-testid="whiteboardToolbar"
      :style="{
        insetInlineEnd: !isSingleRowLayout ? surfaceWindowMobileNotchStore.safeAreaInsetEnd + 'px' : undefined,
        bottom: isSingleRowLayout
          ? isAppUsing('sandboxCardNavigation') && !whiteboardStore.isFrameBreakout
            ? whiteboardStore.frameBarHeight + 8 + 'px' // 8px is the space between the frame bar and the toolbar
            : '0px'
          : undefined,
      }"
      :class="[
        // Position
        'absolute',
        isDoubleColumnLayout && ['w-[92px]'],
        isSingleColumnLayout && [
          'w-[52px]',
          'max-h-full',
          'overflow-x-hidden',
          'overflow-y-auto',
          'py-4',
          'box-border',
          {
            'scrollbar-thin-dark': colorScheme === 'dark',
            'scrollbar-thin': colorScheme === 'light',
          },
        ],
        isSingleRowLayout && [
          !isAppUsing('sandboxCardNavigation') ? 'bottom-0' : '',
          isAppUsing('sandboxCardNavigation') ? 'px-1' : 'px-4 pb-1.5',
          'start-1/2',
          '-translate-s-1/2',
          'flex-row',
          'max-w-full',
          'overflow-x-auto',
          'overflow-y-hidden',
          'box-border',
        ],
        !isSingleRowLayout && ['flex-col'],

        'z-edit-post',

        // Children positioning
        'flex',

        'gap-2',
      ]"
      @pointerdown="$emit('pointerdown', $event)"
    >
      <OzBox
        v-for="(toolSection, sectionIndex) in whiteboardToolbarStore.displayedToolSections"
        :key="'tool-section-' + sectionIndex"
        :radius="0"
        :color="OzBoxColors.None"
        :dark-mode="colorScheme === 'dark'"
        :style="
          isAppUsing('sandboxCardNavigation')
            ? { background: whiteboardStore.frameBarAndToolbarBackground, backdropFilter: 'blur(6px)' }
            : undefined
        "
        :class="[
          // Color
          !isAppUsing('sandboxCardNavigation') && {
            'bg-popover-dark': colorScheme === 'dark',
            'bg-popover-light': colorScheme === 'light',
            'backdrop-blur-10': true,
          },

          // Shape
          !isLoadingPortraitMobileLayout && ['rounded-s-xl'],
          isLoadingPortraitMobileLayout && ['rounded-xl'],
          !isLoadingPortraitMobileLayout &&
            !surfaceWindowMobileNotchStore.isApplyingSafeAreaInsetEnd && ['rounded-e-none'],
          !isLoadingPortraitMobileLayout &&
            surfaceWindowMobileNotchStore.isApplyingSafeAreaInsetEnd && ['rounded-e-xl'],

          'p-1',

          // Layout
          isSingleRowLayout && ['flex', 'flex-row', 'gap-1'],
        ]"
      >
        <div
          v-for="(toolRow, rowIndex) in toolSection"
          :key="'tool-section-row' + rowIndex"
          :class="[
            // Reverse the first section in single row layout since we want the 'Select'
            // tool to be easily accessible (nearer to the center).
            isSingleRowLayout && sectionIndex === 0 && ['flex', 'flex-row-reverse', 'gap-1'],
            isSingleRowLayout && sectionIndex > 0 && ['flex', 'flex-row', 'gap-1'],

            isSingleColumnLayout && ['flex', 'flex-col', 'gap-1', 'items-center'],

            // Double column layout uses a 2D grid.
            isDoubleColumnLayout && toolRow.length === 2 && ['grid', 'grid-cols-2', 'gap-1'],
          ]"
        >
          <WhiteboardToolbarIcon
            v-for="tool in toolRow"
            :key="`${tool.id}`"
            :tool-id="tool.id"
            :test-id="`whiteboardToolbarTool-${tool.id}`"
            :is-expanded-select-tool="
              // The Select tool has a special icon when it's the only tool in a double column layout.
              tool.id === 'select' && toolRow.length === 1 && isDoubleColumnLayout
            "
            :is-selected="tool.id === whiteboardStore.selectedToolId"
            :color-scheme="colorScheme"
            :color-style="!!whiteboardStore.selectedSecondaryTool ? 'opaque' : 'frosted'"
            :icon-name="tool.iconName"
            :secondary-menu="tool.secondaryMenu"
            :secondary-selection-id="whiteboardStore.toolStates[tool.id].currentSecondarySelectionId"
            :current-tool-color="whiteboardStore.toolCurrentColors[tool.id]"
            :label="tool.label"
            :shortcut="tool.shortcut"
            :x-tooltip="xTooltip && tooltipToolId === tool.id"
            :disabled="toolDisabledState[tool.id]"
            @click="handleSelectTool($event, tool.id)"
            @hoveron="trackToolHoverOn(tool.id)"
            @hoveroff="trackToolHoverOff"
          />
        </div>
      </OzBox>
    </div>
  </Transition>
</template>

<style lang="postcss" scoped>
.slide-enter-active {
  transition: all 0.3s cubic-bezier(0, 0, 0.2, 1);

  @media (prefers-reduced-motion) {
    transition: none;
  }
}

.slide-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);

  @media (prefers-reduced-motion) {
    transition: none;
  }
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translateX(100%);

  [dir='rtl'] & {
    transform: translateX(-100%);
  }
}

.slide-leave,
.slide-enter-to {
  opacity: 1;
  transform: translateX(0);
}
</style>
